import { Helmet } from "react-helmet-async";

const SITE_URL = "https://pasta-base.cl";

/**
 * SEO component that renders meta tags + JSON-LD schema.
 *
 * Props:
 *  - title, description, path (required)
 *  - schema: object | array (JSON-LD, optional)
 *  - type: og:type (default 'website')
 */
export default function Seo({
  title,
  description,
  path = "/",
  type = "website",
  schema = null,
}) {
  const url = `${SITE_URL}${path}`;
  const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="pasta-base.cl" />
      <meta property="og:locale" content="es_CL" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <html lang="es-CL" />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

export const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "pasta-base.cl",
  url: SITE_URL,
  parentOrganization: {
    "@type": "Organization",
    name: "SinAdicciones.org",
    url: "https://sinadicciones.org",
  },
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "pasta-base.cl",
  url: SITE_URL,
  description:
    "Información completa sobre pasta base en Chile: qué es, efectos, tratamientos y ayuda profesional.",
  inLanguage: "es-CL",
  publisher: {
    "@type": "Organization",
    name: "SinAdicciones.org",
    url: "https://sinadicciones.org",
  },
};

export function articleSchema({ title, description, path, datePublished = "2026-02-01" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    author: { "@type": "Organization", name: "pasta-base.cl" },
    publisher: {
      "@type": "Organization",
      name: "SinAdicciones.org",
      url: "https://sinadicciones.org",
    },
    datePublished,
    dateModified: datePublished,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}${path}`,
    },
    inLanguage: "es-CL",
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function faqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: { "@type": "Answer", text: q.answer },
    })),
  };
}
